import { Track } from "@/src/lib/tracking";
/**
 * User State Manager - 用户状态管理
 *
 * 负责管理用户切换、用户数据获取和激活数据更新
 */

import type { FavoritesCenterState, FolderInfo, UserData } from "../types";
import { rootStore } from "../../rootStore";
import { updateVersionedData } from "./VersionedDataManager";

export class UserStateManager {
  constructor(
    private sourceData: Map<string, UserData>,
    private state: FavoritesCenterState,
  ) {}

  /**
   * Get current logged-in user ID from rootStore
   */
  getCurrentUserId(): string | null {
    return rootStore.user.ownerInfo?.id || null;
  }

  /**
   * Set current user ID and trigger reactive update
   */
  setCurrentUserId(
    userId: string | null,
    onUserIdChange: (newUserId: string | null) => void,
  ): void {
    Track.log("info", "UserStateManager", `Set user ID: ${userId}`);
    this.state.currentUserId = userId;
    onUserIdChange(userId);
  }

  /**
   * Handle user switch - core method
   */
  async handleUserSwitch(
    currentUserId: string | null,
    setCurrentUserId: (userId: string | null) => void,
  ): Promise<void> {
    const currentLoginUserId = this.getCurrentUserId();

    Track.log("info", "UserStateManager", "handleUserSwitch:", {
      cause: String(currentLoginUserId),
    });
    // Case 1: Not logged in, but currUserId has value
    if (!currentLoginUserId && currentUserId) {
      setCurrentUserId(null);
      return;
    }

    // Case 2: Logged in, but currUserId doesn't exist
    if (currentLoginUserId && !currentUserId) {
      setCurrentUserId(currentLoginUserId);
      return;
    }

    // Case 3: Logged in, but user ID doesn't match
    if (currentLoginUserId && currentUserId !== currentLoginUserId) {
      setCurrentUserId(currentLoginUserId);
      return;
    }

    // Case 4: User matches, no action needed
  }

  /**
   * Get or create user data
   */
  getOrCreateUserData(userId: string): UserData {
    if (!this.sourceData.has(userId)) {
      const userData: UserData = {
        userId,
        userFolders: undefined,
        favoriteStates: new Map(),
        folderPaginationData: new Map(),
        allFavoriteData: undefined,
        lastAccess: Date.now(),
      };

      this.sourceData.set(userId, userData);
    }

    return this.sourceData.get(userId)!;
  }

  /**
   * Update active data from source data
   */
  updateActiveDataFromSource(userId: string): void {
    const userData = this.sourceData.get(userId);

    if (userData) {
      // Restore from source data (use deep copy to avoid reference sharing)
      this.state.userFolders = userData.userFolders
        ? [...userData.userFolders]
        : userData.userFolders;

      // Convert Map data to object format and update versioned data
      updateVersionedData(
        this.state.favoriteStates,
        Object.fromEntries(userData.favoriteStates),
        true,
      );

      if (userData.allFavoriteData && userData.allFavoriteData.size > 0) {
        this.state.allFavoriteData = {
          data: Object.fromEntries(userData.allFavoriteData),
          version: 0,
          lastUpdated: Date.now(),
        };
      } else {
        this.state.allFavoriteData = undefined;
      }
    } else {
      // New user, initialize data
      this.clearActiveData();
    }
  }

  /**
   * Clear active data
   */
  clearActiveData(): void {
    this.state.userFolders = undefined;
    // Clear versioned data
    updateVersionedData(this.state.favoriteStates, {}, true);
    this.state.allFavoriteData = undefined;
  }

  /**
   * Update user folders in state
   */
  updateUserFolders(folders: FolderInfo[]): void {
    this.state.userFolders = folders;
  }
}
